import React, { useState, useRef } from 'react';
import { Paper, Text, Button, Group, Stack, Loader, Alert, SegmentedControl } from '@mantine/core';
import { IconAlertCircle, IconMap, IconSatellite } from '@tabler/icons-react';
import { FormattedMessage, useIntl } from 'react-intl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import './EudrMapSelector.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * EudrMapSelector Component
 * 
 * Interactive map allowing users to:
 * - Click to select a single GPS point
 * - Draw a polygon for a field boundary
 */
const EudrMapSelector = ({ onLocationSelect, disabled }) => {
  const intl = useIntl();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const satelliteLayerRef = useRef(null);
  const streetLayerRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapType, setMapType] = useState('satellite'); // 'satellite' or 'street'

  // Initialize map
  React.useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      // Initialize map centered on South America
      const map = L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: true,
        trackResize: true,
        boxZoom: true,
        doubleClickZoom: false,
        dragging: true,
        keyboard: false,
        scrollWheelZoom: true,
        tap: true,
        touchZoom: true,
        tapTolerance: 15
      }).setView([-15, -60], 4);
      
      // Store the map instance
      mapInstanceRef.current = map;
      
      // Create Satellite imagery layer (Esri World Imagery)
      const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 19
      });
      satelliteLayerRef.current = satelliteLayer;
      
      // Create Street/Topographic layer (OpenStreetMap)
      const streetLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      });
      streetLayerRef.current = streetLayer;
      
      // Add default layer (satellite)
      satelliteLayer.addTo(map);

      // Initialize FeatureGroup for drawn items
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      drawnItemsRef.current = drawnItems;

      // Add drawing controls with custom position
      const drawControl = new L.Control.Draw({
        position: 'topright',
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true,
            drawError: {
              color: '#e1e100',
              message: '<strong>Error:</strong> Polygon edges cannot cross!'
            },
            shapeOptions: {
              color: '#2563eb',
              weight: 2,
              fillOpacity: 0.2
            },
            icon: new L.DivIcon({
              iconSize: new L.Point(8, 8),
              className: 'leaflet-div-icon leaflet-editing-icon'
            }),
            touchIcon: new L.DivIcon({
              iconSize: new L.Point(20, 20),
              className: 'leaflet-div-icon leaflet-editing-icon leaflet-touch-icon'
            })
          },
          polyline: false,
          circle: false,
          circlemarker: false,
          rectangle: false,
          marker: {
            icon: new L.Icon.Default()
          }
        },
        edit: {
          featureGroup: drawnItems,
          remove: true
        }
      });
      map.addControl(drawControl);

      // Handle drawn shapes
      map.on(L.Draw.Event.CREATED, (event) => {
        const layer = event.layer;
        const type = event.layerType;

        // Clear previous drawings
        drawnItems.clearLayers();
        drawnItems.addLayer(layer);

        if (type === 'marker') {
          // Single point
          const latLng = layer.getLatLng();
          const location = {
            type: 'point',
            latitude: latLng.lat,
            longitude: latLng.lng
          };
          setSelectedLocation(location);
          if (onLocationSelect) {
            onLocationSelect(location);
          }
          // Keep map focused on the marker
          map.setView(latLng, Math.max(map.getZoom(), 12));
        } else if (type === 'polygon') {
          // Polygon
          const latLngs = layer.getLatLngs()[0];
          const polygon = latLngs.map(point => ({
            lat: point.lat,
            lon: point.lng
          }));
          const location = {
            type: 'polygon',
            polygon: polygon
          };
          setSelectedLocation(location);
          if (onLocationSelect) {
            onLocationSelect(location);
          }
          // Keep map centered on polygon center WITHOUT changing zoom
          const bounds = layer.getBounds();
          const center = bounds.getCenter();
          setTimeout(() => {
            if (map && !map._removed) {
              map.panTo(center, { animate: true, duration: 0.5 });
            }
          }, 100);
        }
      });

      // Handle shape deletion
      map.on(L.Draw.Event.DELETED, () => {
        setSelectedLocation(null);
        if (onLocationSelect) {
          onLocationSelect(null);
        }
      });

      // Ensure map renders correctly after initialization
      setTimeout(() => {
        if (map && map.getContainer() && map._loaded) {
          try {
            map.invalidateSize();
          } catch (error) {
            console.error('Error invalidating map size:', error);
          }
        }
        setIsMapReady(true);
      }, 100);

      // Cleanup
      return () => {
        if (mapInstanceRef.current) {
          try {
            mapInstanceRef.current.remove();
          } catch (error) {
            console.error('Error removing map:', error);
          }
          mapInstanceRef.current = null;
        }
      };
    }
  }, []); // Empty dependency array - only initialize once

  // Handle map type switching
  React.useEffect(() => {
    if (mapInstanceRef.current && satelliteLayerRef.current && streetLayerRef.current) {
      const map = mapInstanceRef.current;
      
      if (mapType === 'satellite') {
        map.removeLayer(streetLayerRef.current);
        map.addLayer(satelliteLayerRef.current);
      } else {
        map.removeLayer(satelliteLayerRef.current);
        map.addLayer(streetLayerRef.current);
      }
    }
  }, [mapType]);

  const handleClearSelection = () => {
    if (drawnItemsRef.current) {
      drawnItemsRef.current.clearLayers();
      setSelectedLocation(null);
      if (onLocationSelect) {
        onLocationSelect(null);
      }
    }
  };

  return (
    <Paper p="md" withBorder>
      <Stack spacing="md">
        <Group position="apart">
          <Text weight={500} size="lg">
            <FormattedMessage id="eudr.map.title" defaultMessage="Select Location" />
          </Text>
          <Group spacing="xs">
            <SegmentedControl
              value={mapType}
              onChange={setMapType}
              disabled={disabled}
              size="xs"
              data={[
                {
                  value: 'satellite',
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                      <IconSatellite size={14} />
                      <span style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: 'eudrReport.map.satellite', defaultMessage: 'Satellite' })}
                      </span>
                    </div>
                  )
                },
                {
                  value: 'street',
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                      <IconMap size={14} />
                      <span style={{ fontSize: '12px' }}>
                        {intl.formatMessage({ id: 'eudrReport.map.street', defaultMessage: 'Street' })}
                      </span>
                    </div>
                  )
                }
              ]}
            />
            {selectedLocation && (
              <Button 
                variant="outline" 
                color="red" 
                size="xs"
                onClick={handleClearSelection}
                disabled={disabled}
              >
                <FormattedMessage id="eudr.map.clear" defaultMessage="Clear Selection" />
              </Button>
            )}
          </Group>
        </Group>

        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          <Text size="sm">
            <FormattedMessage 
              id="eudr.map.instructions" 
              defaultMessage="Use the map tools to draw a marker (single point) or polygon (field boundary). You can edit or delete your selection." 
            />
          </Text>
        </Alert>

        {selectedLocation && (
          <Alert color="green">
            <Text size="sm" weight={500}>
              {selectedLocation.type === 'point' ? (
                <FormattedMessage 
                  id="eudr.map.point.selected" 
                  defaultMessage="Point selected: {lat}, {lon}"
                  values={{ 
                    lat: selectedLocation.latitude.toFixed(6), 
                    lon: selectedLocation.longitude.toFixed(6) 
                  }}
                />
              ) : (
                <FormattedMessage 
                  id="eudr.map.polygon.selected" 
                  defaultMessage="Polygon with {count} points selected"
                  values={{ count: selectedLocation.polygon.length }}
                />
              )}
            </Text>
          </Alert>
        )}

        {/* Map Container */}
        <div className="eudr-map-container" style={{ position: 'relative', height: '500px', width: '100%' }}>
          <div 
            ref={mapRef}
            style={{ 
              height: '100%', 
              width: '100%',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}
          />
          {!isMapReady && (
            <div style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 1000
            }}>
              <Loader size="lg" />
            </div>
          )}
        </div>
      </Stack>
    </Paper>
  );
};

export default EudrMapSelector;
